import { EntityDetailShell } from '@startupquickstart/frontend';
import type { Neighborhood } from '@/config/objects/neighborhoods';
import {
  Button,
  ConfirmDialog,
  StatusPill,
  useApiClient,
  toast,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building, Building2, MapPin, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Building },
  { label: 'Hotels', path: 'hotels', icon: Building2 },
] as const;

export function NeighborhoodDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Neighborhood | null>(null);
  const basePath = `/neighborhoods/${id}`;

  const deleteMutation = useMutation({
    mutationFn: async (
      neighborhoodId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/neighborhoods/${neighborhoodId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Neighborhood'] });
        navigate('/neighborhoods');
      } else {
        throw new Error(response.error || 'Failed to delete neighborhood');
      }
    },
  });

  const handleDelete = () => {
    if (!pendingDelete) return;
    toast.promise(deleteMutation.mutateAsync(pendingDelete.id), {
      loading: 'Deleting neighborhood...',
      success: 'Neighborhood deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete neighborhood',
    });
  };

  return (
    <>
      <EntityDetailShell<Neighborhood>
        resource="neighborhoods"
        queryKey="Neighborhood"
        id={id}
        basePath={basePath}
        entityLabel="Neighborhood"
        tabs={tabs}
        contextKey="neighborhood"
        hero={{
          category: { label: 'Neighborhood', icon: Building },
          title: (n) => n?.name || 'Neighborhood',
          imageUrl: (n) => n?.featuredImage?.url ?? null,
          fallbackIcon: Building,
          imageAlt: (n) => n?.name || 'Neighborhood',
          meta: (n) => (
            <>
              {n.greaterArea && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {n.greaterArea.name}
                </span>
              )}
              {(n.city || n.state) && (
                <span className="flex items-center gap-1">
                  {[n.city, n.state].filter(Boolean).join(', ')}
                </span>
              )}
            </>
          ),
          badges: (n) => (n.status ? <StatusPill status={n.status} /> : null),
        }}
        breadcrumbs={(n) => [
          { label: 'Home', href: '/' },
          { label: 'Neighborhoods', href: '/neighborhoods' },
          { label: n?.name || 'Neighborhood', current: true },
        ]}
        headerActions={(n) =>
          n ? (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setPendingDelete(n);
                  setShowDeleteConfirm(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Neighborhood"
        description={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
